<script setup lang="ts">
import { computed } from 'vue';

import type { ChartGranularity, ChartPoint } from './chart-point-window';

import { formatChartAxisLabel, formatChartPointWindow } from './chart-point-window';
import { buildLabelIndexes } from './chart-scales';
import ChartCanvas from './ChartCanvas.vue';
import ChartTooltip from './ChartTooltip.vue';
import { useBarChartScale } from './use-bar-chart-scale';
import { useChartCanvas } from './use-chart-canvas';

interface Props {
  current: ChartPoint[];
  previous?: ChartPoint[];
  granularity?: ChartGranularity;
  label?: string;
  currentLabel?: string;
  incompleteCurrentIndex?: number;
  incompletePreviousIndex?: number;
  /** Текст подсказки вместо «день не закончен» / «период не завершён». */
  incompleteHint?: string;
  previousLabel?: string;
  valueFormatter?: (value: number) => string;
}

defineSlots<{
  tooltip?: (props: {
    date: string;
    current: ChartPoint | undefined;
    previous: ChartPoint | undefined;
    isIncompleteCurrent: boolean;
    isIncompletePrevious: boolean;
  }) => unknown;
}>();

const props = withDefaults(defineProps<Props>(), {
  previous: () => [],
  granularity: 'Day',
  label: 'Сравнение показателя по дням',
  currentLabel: 'Текущий период',
  incompleteCurrentIndex: -1,
  incompletePreviousIndex: -1,
  incompleteHint: undefined,
  previousLabel: 'Прошлый период',
  valueFormatter: (value: number) => Intl.NumberFormat('ru-RU').format(value),
});

const pointsCount = computed(() => Math.max(props.current.length, props.previous.length));
const { activeIndex, canvas, geometry } = useChartCanvas('bar-chart', () => props.label, pointsCount);

const { valueScale, valueTicks, pointSlotWidth, centerScale, barHeight } = useBarChartScale(
  () => [...props.current.map((point) => point.value), ...props.previous.map((point) => point.value)],
  pointsCount,
  geometry,
);
const barWidth = computed(() => Math.max(0.8, Math.min(18, pointSlotWidth.value * 0.3)));

const groups = computed(() =>
  Array.from({ length: pointsCount.value }, (_, index) => {
    const centerX = centerScale.value(index);
    const current = props.current[index];
    const previous = props.previous[index];
    const currentHeight = barHeight(current?.value ?? 0);
    const previousHeight = barHeight(previous?.value ?? 0);

    return {
      index,
      centerX,
      current,
      previous,
      isIncompleteCurrent: current?.incomplete === true || index === props.incompleteCurrentIndex,
      isIncompletePrevious: previous?.incomplete === true || index === props.incompletePreviousIndex,
      currentHeight,
      previousHeight,
      currentX: centerX + 0.75,
      previousX: centerX - barWidth.value - 0.75,
    };
  }),
);

const activeGroup = computed(() => (activeIndex.value === null ? null : (groups.value[activeIndex.value] ?? null)));
const activeDate = computed(() => activeGroup.value?.current?.date ?? activeGroup.value?.previous?.date ?? null);
const visibleLabelIndexes = computed(() => buildLabelIndexes(props.current.length));
const incompleteHint = computed(
  () => props.incompleteHint ?? (props.granularity === 'Day' ? 'день не закончен' : 'период не завершён'),
);
</script>

<template>
  <ChartCanvas
    v-bind="canvas"
    :geometry="geometry"
    :ticks="valueTicks"
    :scale="valueScale"
    :formatter="valueFormatter"
  >
    <g
      v-for="group in groups"
      :key="group.index"
    >
      <rect
        v-if="group.previous"
        :x="group.previousX"
        :y="geometry.plotBottom - group.previousHeight"
        :width="barWidth"
        :height="group.previousHeight"
        rx="1.5"
        fill="var(--surface-strong)"
        :opacity="group.isIncompletePrevious ? 0.45 : 1"
      />

      <rect
        v-if="group.current"
        :x="group.currentX"
        :y="geometry.plotBottom - group.currentHeight"
        :width="barWidth"
        :height="group.currentHeight"
        rx="1.5"
        fill="var(--accent)"
        :opacity="group.isIncompleteCurrent ? 0.45 : 1"
      />

      <text
        v-if="group.current && visibleLabelIndexes.has(group.index)"
        :x="group.centerX"
        :y="geometry.xLabelY"
        text-anchor="middle"
        fill="var(--text-secondary)"
        font-size="12"
      >
        {{ formatChartAxisLabel(group.current.date, granularity) }}
      </text>
    </g>
    <template #tooltip>
      <ChartTooltip
        v-if="activeGroup && activeDate"
        :anchor-percent="(activeGroup.centerX / geometry.viewWidth) * 100"
      >
        <slot
          name="tooltip"
          :date="activeDate"
          :current="activeGroup.current"
          :previous="activeGroup.previous"
          :is-incomplete-current="activeGroup.isIncompleteCurrent"
          :is-incomplete-previous="activeGroup.isIncompletePrevious"
        >
          <div class="grid gap-1">
            <p class="text-(--text-secondary)">{{ formatChartPointWindow(activeDate, granularity) }}</p>
            <p v-if="activeGroup.current">
              {{ currentLabel }}: {{ valueFormatter(activeGroup.current.value) }}
              <span v-if="activeGroup.isIncompleteCurrent"> · {{ incompleteHint }}</span>
            </p>
            <p v-if="activeGroup.previous">
              {{ previousLabel }}: {{ valueFormatter(activeGroup.previous.value) }}
              <span v-if="activeGroup.isIncompletePrevious"> · {{ incompleteHint }}</span>
            </p>
          </div>
        </slot>
      </ChartTooltip>
    </template>
  </ChartCanvas>
</template>
